import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Music, Calendar, Hash, Clock, User, ExternalLink, Image as ImageIcon, Save, Edit, CheckCircle, Loader2, AlertCircle, ChevronDown, Mail, Phone } from 'lucide-react';
import type { Release, Artist, Campaign, CreateReleaseData, UpdateReleaseData, ContactContact } from '../../../shared/types/entities';
import { releaseService } from '../../../backend/services/releases';
import { spotifyService } from '../../../backend/services/spotify';
import { campaignService } from '../../../backend/services/campaigns';
import { useArtists } from '../../hooks/useArtists';
import { toast } from 'sonner';
import { supabase } from '../../../backend/services/supabase';
import { contactService } from '../../../backend/services/contacts';
import { releaseContactsService } from '../../../backend/services/releaseContacts';
type ModalState = 'view' | 'edit' | 'create';
type ModalMode = 'view' | 'edit' | 'create';

interface UnifiedReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  artists: Artist[];
  clients: any[]; // Client type from contacts
  release?: Release | null;
  mode?: ModalMode;
  onCreated?: (release: Release) => void;
  onUpdated?: (release: Release) => void;
  onArtistCreated?: (artist: Artist) => void;
}

interface FormData {
  artist_id: string;
  title: string;
  release_date: string;
  release_type: 'single' | 'ep' | 'album';
  track_count: number;
  duration_ms: number;
  spotify_url: string;
  artwork_url: string;
  spotify_track_id?: string;
  spotify_track_name?: string;
}

interface FieldState {
  [key: string]: 'idle' | 'saving' | 'saved' | 'error';
}

interface TeamContact {
  linkId: string;
  contactId: string;
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  isPrimary?: boolean;
  clientName?: string;
}

const initialFormData: FormData = {
  artist_id: '',
  title: '',
  release_date: '',
  release_type: 'single',
  track_count: 1,
  duration_ms: 0,
  spotify_url: '',
  artwork_url: '',
  spotify_track_id: '',
  spotify_track_name: ''
};

export const UnifiedReleaseModal: React.FC<UnifiedReleaseModalProps> = ({
  isOpen,
  onClose,
  artists,
  clients,
  release,
  mode = 'view',
  onCreated,
  onUpdated,
  onArtistCreated
}) => {
  const [modalState, setModalState] = useState<ModalState>(mode);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [fieldState, setFieldState] = useState<FieldState>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [spotifyLoading, setSpotifyLoading] = useState(false);
  const [spotifyError, setSpotifyError] = useState<string | null>(null);
  
  // New artist creation state
  const [showNewArtistDialog, setShowNewArtistDialog] = useState(false);
  const [newArtistData, setNewArtistData] = useState<{
    name: string;
    spotify_id: string;
    spotify_url: string;
    image_url?: string;
    followers_count?: number;
    genre?: string;
  } | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [isCreatingArtist, setIsCreatingArtist] = useState(false);
  
  // Duplicate detection state
  const [duplicateRelease, setDuplicateRelease] = useState<Release | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [proceedWithDuplicate, setProceedWithDuplicate] = useState(false);

  // Campaign linking modal state
  const [showCampaignLinkModal, setShowCampaignLinkModal] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState<Campaign[]>([]);
  const [campaignLinkLoading, setCampaignLinkLoading] = useState(false);
  const [campaignLinkError, setCampaignLinkError] = useState<string | null>(null);
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  
  const { createArtist } = useArtists();
  const navigate = useNavigate();
  
  const modalRef = useRef<HTMLDivElement>(null);
  const timeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Add new state in component
  const [expandedSections, setExpandedSections] = useState({
    core: true,
    metadata: false,
    campaigns: false,
    team: false
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Initialize form data when modal opens or release changes
  useEffect(() => {
    if (!isOpen) return;
    
    if (release) {
      setFormData({
        artist_id: release.artist_id || '',
        title: release.title || '',
        release_date: release.release_date || '',
        release_type: release.release_type || 'single',
        track_count: release.track_count || 1,
        duration_ms: release.duration_ms || 0,
        spotify_url: release.spotify_url || '',
        artwork_url: release.artwork_url || '',
        spotify_track_id: release.spotify_track_id || '',
        spotify_track_name: release.spotify_track_name || ''
      });
      setModalState('view');
    } else {
      setFormData(initialFormData);
      setModalState('create');
    }
    
    setErrors({});
    setSpotifyError(null);
  }, [isOpen, release]);

  // Initialize selected client from localStorage or first client
  useEffect(() => {
    if (clients.length > 0 && !selectedClientId) {
      const lastClientId = localStorage.getItem('br.lastClientId');
      const defaultClientId = lastClientId || clients[0]?.id || '';
      setSelectedClientId(defaultClientId);
    }
  }, [clients, selectedClientId]);

  // Focus management for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const firstFocusable = modalRef.current.querySelector('[tabindex="0"]') as HTMLElement;
      firstFocusable?.focus();
    }
  }, [isOpen, modalState]);

  // Format duration helper
  const formatDuration = useCallback((ms: number): string => {
    if (!ms || ms <= 0) return '0:00';
    const totalSec = Math.round(ms / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }, []);

  // Parse duration from MM:SS format
  const parseDuration = useCallback((duration: string): number => {
    const match = duration.trim().match(/^(\d+):(\d{1,2})$/);
    if (!match) return 0;
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    return (minutes * 60 + seconds) * 1000;
  }, []);

  // Extract Spotify track ID from URL
  const extractSpotifyTrackId = useCallback((url: string): string | null => {
    const trackMatch = url.match(/spotify\.com\/track\/([a-zA-Z0-9]+)/);
    return trackMatch ? trackMatch[1] : null;
  }, []);

  // Fetch Spotify metadata
  const fetchSpotifyMetadata = useCallback(async (url: string) => {
    if (!url) return;
    
    const trackId = extractSpotifyTrackId(url);
    if (!trackId) {
      setSpotifyError('Invalid Spotify URL format');
      return;
    }

    setSpotifyLoading(true);
    setSpotifyError(null);

    try {
      // Get the user's session token for API authentication
      const { data: { session } } = await supabase.auth.getSession();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`/api/spotify-release?track_id=${encodeURIComponent(trackId)}`, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Spotify credentials not configured');
        }
        throw new Error('Failed to fetch Spotify data');
      }
      
      const data = await response.json();
      const track = data.track;
      
      if (track) {
        // Auto-populate form with Spotify data
        setFormData(prev => ({
          ...prev,
          title: track.name || prev.title,
          duration_ms: track.duration_ms || prev.duration_ms,
          artwork_url: track.album?.images?.[0]?.url || prev.artwork_url,
          spotify_track_id: track.id,
          spotify_track_name: track.name,
          spotify_url: url,
          // Set release date from album release date
          release_date: track.album?.release_date ? 
            new Date(track.album.release_date).toISOString().split('T')[0] : 
            prev.release_date,
          // Set track count from album total tracks
          track_count: track.album?.total_tracks || prev.track_count || 1
        }));

        // Try to match artist
        if (track.artists?.[0]?.name) {
          const matchingArtist = artists.find(artist => 
            artist.name.toLowerCase() === track.artists[0].name.toLowerCase()
          );
          if (matchingArtist) {
            setFormData(prev => ({ ...prev, artist_id: matchingArtist.id }));
          } else {
            // If no matching artist found, offer to create one
            setNewArtistData({
              name: track.artists[0].name,
              spotify_id: track.artists[0].id,
              spotify_url: track.external_urls?.spotify || `https://open.spotify.com/artist/${track.artists[0].id}`,
              image_url: track.album?.images?.[0]?.url,
              followers_count: undefined, // Not available in track data
              genre: undefined // Not available in track data
            });
            setShowNewArtistDialog(true);
          }
        }

        toast.success('Spotify metadata loaded successfully');
      }
    } catch (error) {
      console.error('Spotify fetch error:', error);
      setSpotifyError('Failed to load Spotify metadata');
      toast.error('Failed to load Spotify metadata');
    } finally {
      setSpotifyLoading(false);
    }
  }, [artists, extractSpotifyTrackId]);

  // Check for duplicate releases
  const checkForDuplicates = useCallback(async (data: FormData): Promise<Release | null> => {
    try {
      // Get all releases to check for duplicates
      const releases = await releaseService.list();
      
      // Check for duplicates based on different criteria
      const duplicate = releases.find(existingRelease => {
        // Skip if comparing with the same release (in edit mode)
        if (release && existingRelease.id === release.id) return false;
        
        // 1. Same Spotify URL (strongest indicator)
        if (data.spotify_url && existingRelease.spotify_url && 
            data.spotify_url.trim() === existingRelease.spotify_url.trim()) {
          return true;
        }
        
        // 2. Same artist + same title (case insensitive)
        if (data.artist_id === existingRelease.artist_id && 
            data.title.trim().toLowerCase() === existingRelease.title.trim().toLowerCase()) {
          return true;
        }
        
        // 3. Same Spotify ID (if available)
        if (data.spotify_track_id && existingRelease.spotify_id && 
            data.spotify_track_id === existingRelease.spotify_id) {
          return true;
        }
        
        return false;
      });
      
      return duplicate || null;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return null;
    }
  }, [release]);

  // Handle Spotify URL change
  const handleSpotifyUrlChange = useCallback((url: string) => {
    setFormData(prev => ({ ...prev, spotify_url: url }));
    setSpotifyError(null);
    
    // Auto-fetch if it looks like a valid Spotify URL
    if (url && extractSpotifyTrackId(url)) {
      fetchSpotifyMetadata(url);
    }
  }, [extractSpotifyTrackId, fetchSpotifyMetadata]);

  // Update form field
  const updateField = useCallback((field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  }, []);

  // Debounced field save for existing releases
  const debouncedFieldSave = useCallback((field: keyof FormData, value: any) => {
    if (!release || modalState === 'create') return;

    // Clear existing timeout
    if (timeoutsRef.current[field]) {
      clearTimeout(timeoutsRef.current[field]);
    }

    // Set saving state
    setFieldState(prev => ({ ...prev, [field]: 'saving' }));

    // Debounce the save
    timeoutsRef.current[field] = setTimeout(async () => {
      try {
        await releaseService.update(release.id, { [field]: value } as UpdateReleaseData);
        setFieldState(prev => ({ ...prev, [field]: 'saved' }));
        
        // Clear saved state after 2 seconds
        setTimeout(() => {
          setFieldState(prev => ({ ...prev, [field]: 'idle' }));
        }, 2000);
      } catch (error) {
        console.error(`Failed to save ${field}:`, error);
        setFieldState(prev => ({ ...prev, [field]: 'error' }));
        toast.error(`Failed to save ${field}`);
      }
    }, 1000);
  }, [release, modalState]);

  // Form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Spotify URL is optional, but if provided, must be valid
    if (formData.spotify_url.trim() && !extractSpotifyTrackId(formData.spotify_url)) {
      newErrors.spotify_url = 'Please enter a valid Spotify track URL';
    }
    if (!formData.artist_id.trim()) {
      newErrors.artist_id = 'Artist is required';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }
    if (formData.release_date && isNaN(Date.parse(formData.release_date))) {
      newErrors.release_date = 'Please enter a valid date';
    }
    if (formData.duration_ms < 0) {
      newErrors.duration_ms = 'Duration must be positive';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, extractSpotifyTrackId]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (modalState === 'create') {
        // Check for duplicates before creating (unless user has already confirmed)
        if (!proceedWithDuplicate) {
          const duplicate = await checkForDuplicates(formData);
          if (duplicate) {
            setDuplicateRelease(duplicate);
            setShowDuplicateDialog(true);
            setIsSaving(false);
            return;
          }
        }
        
        const newRelease = await releaseService.create(formData as CreateReleaseData);
        onCreated?.(newRelease);
        toast.success('Release created successfully');
      } else if (release) {
        const updatedRelease = await releaseService.update(release.id, formData as UpdateReleaseData);
        onUpdated?.(updatedRelease);
        toast.success('Release updated successfully');
      }
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save release');
    } finally {
      setIsSaving(false);
      setProceedWithDuplicate(false); // Reset for next time
    }
  }, [modalState, formData, validateForm, release, onCreated, onUpdated, onClose, proceedWithDuplicate, checkForDuplicates]);

  // Handle duplicate dialog actions
  const handleProceedWithDuplicate = useCallback(() => {
    setProceedWithDuplicate(true);
    setShowDuplicateDialog(false);
    // Trigger save again with duplicate bypass
    setTimeout(() => handleSave(), 0);
  }, [handleSave]);

  const handleCancelDuplicate = useCallback(() => {
    setShowDuplicateDialog(false);
    setDuplicateRelease(null);
    setProceedWithDuplicate(false);
  }, []);

  // Handle mode change
  const handleModeChange = useCallback((newMode: ModalState) => {
    setModalState(newMode);
  }, []);

  // Get current artist name
  const currentArtist = useMemo(() => {
    return artists.find(a => a.id === formData.artist_id)?.name || '';
  }, [artists, formData.artist_id]);

  // Check if form can be saved
  const canSave = useMemo(() => {
    return formData.artist_id.trim() && 
           formData.title.trim() && 
           !isSaving;
  }, [formData.artist_id, formData.title, isSaving]);

  // Handle creating new artist
  const handleCreateArtist = async () => {
    if (!newArtistData || !selectedClientId) return;

    setIsCreatingArtist(true);
    try {
      const newArtist = await createArtist({
        client_id: selectedClientId,
        name: newArtistData.name,
        spotify_id: newArtistData.spotify_id,
        spotify_url: newArtistData.spotify_url,
        image_url: newArtistData.image_url,
        followers_count: newArtistData.followers_count,
        genre: newArtistData.genre
      });

      // Update form with new artist
      setFormData(prev => ({ ...prev, artist_id: newArtist.id }));
      
      // Update local artists list
      setLocalArtists(prev => [...prev, newArtist]);
      
      // Close dialog and notify parent
      setShowNewArtistDialog(false);
      setNewArtistData(null);
      onArtistCreated?.(newArtist);
      
      toast.success(`Artist "${newArtist.name}" created successfully`);
    } catch (error) {
      console.error('Failed to create artist:', error);
      toast.error('Failed to create artist. Please try again.');
    } finally {
      setIsCreatingArtist(false);
    }
  };

  // Campaign data state - real loading
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  
  // Other data state
  const [teamContacts, setTeamContacts] = useState<TeamContact[]>([]);
  const [teamContactsLoading, setTeamContactsLoading] = useState(false);
  const [releaseClientId, setReleaseClientId] = useState<string | null>(null);
  const [showContactLinkModal, setShowContactLinkModal] = useState(false);
  const [availableTeamContacts, setAvailableTeamContacts] = useState<ContactContact[]>([]);
  const [contactLinkLoading, setContactLinkLoading] = useState(false);
  const [contactLinkError, setContactLinkError] = useState<string | null>(null);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  // Add local artists state
  const [localArtists, setLocalArtists] = useState(artists);

  const filteredAvailableContacts = useMemo(() => {
    if (!contactSearchQuery) return availableTeamContacts;
    const normalized = contactSearchQuery.toLowerCase();
    return availableTeamContacts.filter((contact) => {
      const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim().toLowerCase();
      return (
        fullName.includes(normalized) ||
        (contact.email?.toLowerCase().includes(normalized) ?? false) ||
        (contact.phone?.toLowerCase().includes(normalized) ?? false)
      );
    });
  }, [availableTeamContacts, contactSearchQuery]);

  // Update local artists when props change
  useEffect(() => {
    setLocalArtists(artists);
  }, [artists]);

  // Load campaigns for this release
  const fetchCampaigns = useCallback(async () => {
    if (!release?.id) return;
    
    try {
      setCampaignsLoading(true);
      setCampaignsError(null);
      
      const campaignData = await campaignService.getCampaignsByReleaseId(release.id);
      setCampaigns(campaignData);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaignsError(error instanceof Error ? error.message : 'Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  }, [release?.id]);

  // Load campaigns when release changes or when modal opens
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  

  const fetchTeamContacts = useCallback(async () => {
    if (!release?.id || !release?.artist_id) {
      setTeamContacts([]);
      setReleaseClientId(null);
      setTeamContactsLoading(false);
      return;
    }

    setTeamContactsLoading(true);

    try {
      let clientId = release.artist?.client_id || null;

      if (!clientId) {
        const { data: artistRecord, error: artistError } = await supabase
          .from('artists')
          .select('client_id')
          .eq('id', release.artist_id)
          .single();

        if (artistError) {
          throw artistError;
        }

        clientId = artistRecord?.client_id || null;
      }

      setReleaseClientId(clientId || null);

      if (!clientId) {
        setTeamContacts([]);
        return;
      }

      const links = await releaseContactsService.listByRelease(release.id);

      const mappedContacts: TeamContact[] = links.map((link) => {
        const contact = link.contact;
        const fullName = contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : '';
        const displayName = fullName || contact?.email || contact?.phone || 'Contact removed';

        return {
          linkId: link.id,
          contactId: link.client_contact_id,
          name: displayName,
          role: link.role || contact?.title || contact?.department || undefined,
          email: contact?.email || undefined,
          phone: contact?.phone || undefined,
          isPrimary: contact?.is_primary ?? undefined,
          clientName: contact?.client?.name,
        };
      });

      setTeamContacts(mappedContacts);
    } catch (error) {
      console.error('Failed to fetch team contacts:', error);
      toast.error('Failed to fetch team contacts');
      setTeamContacts([]);
    } finally {
      setTeamContactsLoading(false);
    }
  }, [release?.id, release?.artist_id, release?.artist?.client_id]);

  const loadAvailableTeamContacts = useCallback(async () => {
    if (!releaseClientId) return;

    try {
      setContactLinkLoading(true);
      setContactLinkError(null);

      const contacts = await contactService.getContactContacts(releaseClientId);
      const linkedIds = new Set(teamContacts.map((contact) => contact.contactId));
      const available = contacts.filter((contact) => !linkedIds.has(contact.id));

      setAvailableTeamContacts(available);
    } catch (error) {
      console.error('Failed to load contacts for linking:', error);
      const message = error instanceof Error ? error.message : 'Failed to load contacts';
      setContactLinkError(message);
      toast.error(message);
    } finally {
      setContactLinkLoading(false);
    }
  }, [releaseClientId, teamContacts]);

  const handleOpenContactLinkModal = useCallback(async () => {
    if (!release?.id) {
      toast.error('Save the release first before linking contacts');
      return;
    }

    if (!releaseClientId) {
      toast.info('Assign this release to an artist with a company to link contacts.');
      return;
    }

    setShowContactLinkModal(true);
    setSelectedContactIds([]);
    setContactSearchQuery('');
    await loadAvailableTeamContacts();
  }, [release?.id, releaseClientId, loadAvailableTeamContacts]);

  const handleToggleContactSelection = useCallback((contactId: string) => {
    setSelectedContactIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  }, []);

  const handleLinkSelectedContacts = useCallback(async () => {
    if (!release?.id || selectedContactIds.length === 0) return;

    try {
      setContactLinkLoading(true);
      setContactLinkError(null);

      for (const contactId of selectedContactIds) {
        await releaseContactsService.linkContact(release.id, contactId);
      }

      toast.success(selectedContactIds.length === 1 ? 'Contact linked to release' : 'Contacts linked to release');
      setShowContactLinkModal(false);
      setSelectedContactIds([]);
      await fetchTeamContacts();
    } catch (error) {
      console.error('Failed to link contacts to release:', error);
      const message = error instanceof Error ? error.message : 'Failed to link contacts';
      setContactLinkError(message);
      toast.error(message);
    } finally {
      setContactLinkLoading(false);
    }
  }, [release?.id, selectedContactIds, fetchTeamContacts]);

  const handleUnlinkContact = useCallback(async (contact: TeamContact) => {
    if (!release?.id) return;

    const confirmed = window.confirm(`Remove ${contact.name} from this release?`);
    if (!confirmed) return;

    try {
      await releaseContactsService.unlinkContact(contact.linkId);
      toast.success('Contact unlinked from release');
      await fetchTeamContacts();
    } catch (error) {
      console.error('Failed to unlink contact from release:', error);
      const message = error instanceof Error ? error.message : 'Failed to unlink contact';
      toast.error(message);
    }
  }, [release?.id, fetchTeamContacts]);

  useEffect(() => {
    if (!expandedSections.team) return;
    fetchTeamContacts();
  }, [expandedSections.team, fetchTeamContacts]);

  useEffect(() => {
    setTeamContacts([]);
    setTeamContactsLoading(false);
    setReleaseClientId(null);
  }, [release?.id]);

  useEffect(() => {
    if (!showContactLinkModal) {
      setContactLinkError(null);
      setContactSearchQuery('');
      setSelectedContactIds([]);
    }
  }, [showContactLinkModal]);

  // Refine campaigns fetch with error handling

  // Add undo state and button
  const [previousFormData, setPreviousFormData] = useState<FormData | null>(null);
  const handleUndo = () => {
    if (previousFormData) setFormData(previousFormData);
  };

  // Open campaign linking modal
  const handleLinkCampaign = useCallback(() => {
    if (!release?.id) {
      toast.error('Save the release first before linking campaigns');
      return;
    }
    
    setShowCampaignLinkModal(true);
    setSelectedCampaignIds([]);
    setCampaignSearchQuery('');
  }, [release?.id]);

  // Load available campaigns for linking
  const loadAvailableCampaigns = useCallback(async () => {
    if (!release?.id) return;

    try {
      setCampaignLinkLoading(true);
      setCampaignLinkError(null);

      // Get all campaigns that are NOT already linked to this release
      const allCampaigns = await campaignService.getCampaigns({ 
        search: campaignSearchQuery || undefined 
      });
      
      // Filter out campaigns already linked to this release
      const currentCampaignIds = campaigns.map(c => c.id);
      const available = allCampaigns.filter(c => 
        !currentCampaignIds.includes(c.id) && 
        (!c.release_id || c.release_id !== release.id)
      );
      
      setAvailableCampaigns(available);
    } catch (error) {
      console.error('Error loading available campaigns:', error);
      setCampaignLinkError(error instanceof Error ? error.message : 'Failed to load campaigns');
      setAvailableCampaigns([]);
    } finally {
      setCampaignLinkLoading(false);
    }
  }, [release?.id, campaigns, campaignSearchQuery]);

  // Auto-search when campaign search query changes or modal opens
  useEffect(() => {
    if (showCampaignLinkModal) {
      const timeoutId = setTimeout(() => {
        loadAvailableCampaigns();
      }, campaignSearchQuery ? 300 : 0); // Immediate load on modal open, debounced search

      return () => clearTimeout(timeoutId);
    }
  }, [campaignSearchQuery, showCampaignLinkModal, loadAvailableCampaigns]);

  // Create a new campaign for this release
  const handleCreateCampaign = useCallback(async () => {
    if (!release?.id || !formData.title) {
      toast.error('Release must be saved before creating campaigns');
      return;
    }

    try {
      setCampaignsLoading(true);
      
      // Generate campaign name based on release
      const campaignName = `${formData.title} - ${currentArtist} Campaign`;
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 30 days from now
      
      const newCampaign = await campaignService.createCampaignForRelease(release.id, {
        name: campaignName,
        budget: 1000, // Default budget
        start_date: startDate,
        end_date: endDate,
        status: 'draft'
      });

      // Refresh campaigns list
      await fetchCampaigns();
      toast.success(`Campaign "${newCampaign.name}" created successfully!`);
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    } finally {
      setCampaignsLoading(false);
    }
  }, [release?.id, formData.title, currentArtist, fetchCampaigns]);

  // Link selected campaigns to this release
  const handleLinkSelectedCampaigns = useCallback(async () => {
    if (!release?.id || selectedCampaignIds.length === 0) return;

    try {
      setCampaignLinkLoading(true);
      setCampaignLinkError(null);

      // Link each selected campaign
      for (const campaignId of selectedCampaignIds) {
        await campaignService.linkCampaignToRelease(campaignId, release.id);
      }

      // Refresh campaigns list and close modal
      await fetchCampaigns();
      setShowCampaignLinkModal(false);
      toast.success(`Successfully linked ${selectedCampaignIds.length} campaign${selectedCampaignIds.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error linking campaigns:', error);
      setCampaignLinkError(error instanceof Error ? error.message : 'Failed to link campaigns');
    } finally {
      setCampaignLinkLoading(false);
    }
  }, [release?.id, selectedCampaignIds, fetchCampaigns]);

  // Unlink a campaign from this release
  const handleUnlinkCampaign = useCallback(async (campaignId: string, campaignName: string) => {
    if (!window.confirm(`Are you sure you want to unlink "${campaignName}" from this release?`)) {
      return;
    }

    try {
      setCampaignsLoading(true);
      await campaignService.unlinkCampaignFromRelease(campaignId);
      await fetchCampaigns();
      toast.success(`Campaign "${campaignName}" unlinked successfully`);
    } catch (error) {
      console.error('Error unlinking campaign:', error);
      toast.error('Failed to unlink campaign');
    } finally {
      setCampaignsLoading(false);
    }
  }, [fetchCampaigns]);

  // Quick actions
  const handleArchive = async () => {
    if (release) {
      await releaseService.update(release.id, { archived: true });
      toast.success('Release archived');
      onClose();
    }
  };

  const handleDuplicate = async () => {
    if (release) {
      const dupData = { ...formData, title: `${formData.title} (Copy)` };
      const newRelease = await releaseService.create(dupData);
      toast.success('Release duplicated');
      onCreated?.(newRelease);
    }
  };

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/60 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div 
          ref={modalRef}
          className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Music className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                {modalState === 'create' ? (
                  <h2 id="modal-title" className="text-xl font-semibold text-gray-900 dark:text-white">
                    Add Release
              </h2>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center gap-3 min-w-0">
                      <h2 id="modal-title" className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                        {formData.title || release?.title || 'Untitled Release'}
                      </h2>
                      <span className="text-gray-400 dark:text-gray-500">•</span>
                      <button
                        onClick={() => {
                          if (formData.artist_id) {
                            // Navigate to artists page with specific artist selected
                            navigate(`/artists?artist_id=${formData.artist_id}`);
                          }
                        }}
                        className="text-lg font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors truncate"
                        title="View artist details"
                      >
                        {currentArtist || release?.artist?.name || 'Unknown Artist'}
                      </button>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                      <span className="capitalize font-medium">{formData.release_type}</span>
                      <span>•</span>
                      <span>{formatDuration(formData.duration_ms)}</span>
                      {formData.release_date && (
                        <>
                          <span>•</span>
                          <span>{new Date(formData.release_date).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {release && modalState !== 'create' && (
                <button
                  onClick={() => handleModeChange(modalState === 'view' ? 'edit' : 'view')}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title={modalState === 'view' ? 'Edit release' : 'View release'}
                >
                  {modalState === 'view' ? <Edit className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Close modal"
              >
                <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content - Two Column Layout */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 h-full">
              
              {/* Left Column - Hero Section (40%) */}
              <div className="lg:col-span-2 space-y-6">
                {/* Large Artwork */}
                <div className="w-full max-w-sm mx-auto lg:mx-0">
                  <div className="aspect-square bg-gray-200 dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg">
                  {formData.artwork_url ? (
                    <img 
                      src={formData.artwork_url} 
                      alt={`${formData.title} artwork`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-16 h-16 text-gray-400 dark:text-gray-600" />
                    </div>
                  )}
                </div>

                  {/* Compact Spotify Player - Directly Under Artwork */}
                  {(formData.spotify_track_id || formData.spotify_url) && (
                    <div className="mt-4 bg-gray-900 rounded-xl overflow-hidden shadow-lg">
                      <iframe
                        src={`https://open.spotify.com/embed/track/${formData.spotify_track_id || extractSpotifyTrackId(formData.spotify_url) || ''}?utm_source=generator&theme=0`}
                        width="100%"
                        height="80"
                        frameBorder="0"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        className="rounded-xl"
                      />
                      <div className="px-3 py-2">
                        <a
                          href={formData.spotify_url || `https://open.spotify.com/track/${formData.spotify_track_id || extractSpotifyTrackId(formData.spotify_url) || ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-xs text-green-400 hover:text-green-300 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open in Spotify
                        </a>
                    </div>
                  </div>
                )}
              </div>


                {/* Compact Spotify Player */}
              </div>

              {/* Right Column - Action Cards (60%) */}
              <div className="lg:col-span-3 space-y-6">
                
                {/* Edit Form Card - FIRST in Create Mode */}
                {modalState !== 'view' && (
                  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {modalState === 'create' ? 'Release Details' : 'Edit Release'}
                      </h3>
                    </div>
                    
                    <div className="space-y-6">
                      {/* Spotify URL - Primary Entry Point (Prominent in Create Mode) */}
                      <div className={modalState === 'create' ? 'bg-gradient-to-r from-green-50 to-purple-50 dark:from-green-900/20 dark:to-purple-900/20 p-4 rounded-xl border-2 border-dashed border-green-300 dark:border-green-700' : ''}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {modalState === 'create' ? (
                            <span className="text-lg font-semibold text-green-700 dark:text-green-300">
                              🎵 Start with Spotify URL (Recommended)
                            </span>
                          ) : (
                            '🎵 Spotify URL (Auto-populates data)'
                          )}
                      </label>
                        {modalState === 'create' && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            Paste a Spotify track URL to automatically populate title, artist, artwork, duration, and release date
                          </p>
                        )}
                      <div className="relative">
                        <input
                          type="url"
                          value={formData.spotify_url}
                          onChange={(e) => handleSpotifyUrlChange(e.target.value)}
                          onPaste={(e) => {
                            const pastedText = e.clipboardData.getData('text');
                            if (pastedText.includes('spotify.com')) {
                              e.preventDefault();
                              handleSpotifyUrlChange(pastedText);
                            }
                          }}
                          placeholder="https://open.spotify.com/track/..."
                            className={`w-full rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 pr-20 focus:ring-2 focus:border-purple-500 ${
                              modalState === 'create' 
                                ? 'border-green-300 dark:border-green-600 focus:ring-green-500 text-lg' 
                                : 'border-gray-300 dark:border-gray-600 focus:ring-purple-500'
                            }`}
                        />
                        {spotifyLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <Loader2 className="w-4 h-4 animate-spin text-green-500" />
                          </div>
                        )}
                      </div>
                      {spotifyError && (
                        <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {spotifyError}
                        </p>
                      )}
                      {formData.spotify_url && !spotifyError && (
                        <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                            Metadata loaded successfully! ✨
                        </p>
                      )}
                    </div>

                      {/* Core Fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Artist *
                        </label>
                        <select
                          value={formData.artist_id}
                          onChange={(e) => {
                            updateField('artist_id', e.target.value);
                            if (release) debouncedFieldSave('artist_id', e.target.value);
                          }}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="">Select artist...</option>
                            {localArtists.filter(a => a.name).map(artist => (
                            <option key={artist.id} value={artist.id}>
                              {artist.name}
                            </option>
                          ))}
                        </select>
                        {errors.artist_id && (
                          <p className="text-sm text-red-600 mt-1">{errors.artist_id}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Title *
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => {
                            updateField('title', e.target.value);
                            if (release) debouncedFieldSave('title', e.target.value);
                          }}
                          placeholder="Song/Album title"
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        {errors.title && (
                          <p className="text-sm text-red-600 mt-1">{errors.title}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Release Type
                        </label>
                        <select
                          value={formData.release_type}
                          onChange={(e) => {
                            updateField('release_type', e.target.value);
                            if (release) debouncedFieldSave('release_type', e.target.value);
                          }}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="single">Single</option>
                          <option value="ep">EP</option>
                          <option value="album">Album</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Release Date
                        </label>
                        <input
                          type="date"
                          value={formData.release_date}
                          onChange={(e) => {
                            updateField('release_date', e.target.value);
                            if (release) debouncedFieldSave('release_date', e.target.value);
                          }}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        {errors.release_date && (
                          <p className="text-sm text-red-600 mt-1">{errors.release_date}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Artwork URL
                      </label>
                      <input
                        type="url"
                        value={formData.artwork_url}
                        onChange={(e) => {
                          updateField('artwork_url', e.target.value);
                          if (release) debouncedFieldSave('artwork_url', e.target.value);
                        }}
                        placeholder="https://.../cover.jpg"
                          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      </div>

                    </div>
                  </div>
                )}

                {/* Campaign Cards - Most Important */}
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Associated Campaigns
                      </h3>
                      {campaigns.length > 0 && (
                        <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded-full text-xs font-medium">
                          {campaigns.length}
                        </span>
                      )}
                          </div>
                    <button
                      onClick={handleLinkCampaign}
                      className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                    >
                      + Link Campaign
                    </button>
                      </div>
                      
                  {campaignsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                      <span className="ml-2 text-gray-500 dark:text-gray-400">Loading campaigns...</span>
                          </div>
                  ) : campaignsError ? (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                      <p className="text-red-600 dark:text-red-400 mb-4 text-sm">
                        {campaignsError}
                      </p>
                      <button
                        onClick={fetchCampaigns}
                        className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                      >
                        Try again
                      </button>
                        </div>
                  ) : campaigns.length > 0 ? (
                    <div className="space-y-3">
                      {campaigns.slice(0, 3).map(campaign => (
                        <div key={campaign.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${
                                campaign.status === 'active' ? 'bg-green-500' : 
                                campaign.status === 'paused' ? 'bg-yellow-500' : 
                                'bg-gray-400'
                              }`}></div>
                              <h4 className="font-medium text-gray-900 dark:text-white">{campaign.name}</h4>
                      </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">{campaign.status}</span>
                    </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                              <span className="text-gray-500 dark:text-gray-400">Budget</span>
                              <div className="font-medium text-gray-900 dark:text-white">${campaign.budget?.toLocaleString() || '0'}</div>
                          </div>
                        <div>
                              <span className="text-gray-500 dark:text-gray-400">Spend</span>
                              <div className="font-medium text-gray-900 dark:text-white">${campaign.spend?.toLocaleString() || '0'}</div>
                        </div>
                        <div>
                              <span className="text-gray-500 dark:text-gray-400">CTR</span>
                              <div className="font-medium text-gray-900 dark:text-white">{campaign.ctr ? `${campaign.ctr}%` : '—'}</div>
                      </div>
                        </div>
                          {campaign.budget && campaign.spend && (
                            <div className="mt-3">
                              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                <span>Budget Progress</span>
                                <span>{Math.round((campaign.spend / campaign.budget) * 100)}%</span>
                      </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div 
                                  className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${Math.min((campaign.spend / campaign.budget) * 100, 100)}%` }}
                                ></div>
                    </div>
                  </div>
                          )}
                          
                          {/* Campaign actions */}
                          <div className="flex justify-between items-center text-xs mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                            <span className="text-gray-500 dark:text-gray-400">
                              {campaign.start_date && new Date(campaign.start_date).toLocaleDateString()}
                              {campaign.end_date && ` - ${new Date(campaign.end_date).toLocaleDateString()}`}
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => navigate(`/campaigns?campaign_id=${campaign.id}`)}
                                className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                              >
                                View
                              </button>
                              <span className="text-gray-300 dark:text-gray-600">|</span>
                              <button
                                onClick={() => handleUnlinkCampaign(campaign.id, campaign.name)}
                                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                disabled={campaignsLoading}
                              >
                                Unlink
                              </button>
                          </div>
                        </div>
                      </div>
                      ))}
                      {campaigns.length > 3 && (
                        <button className="w-full text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 py-2">
                          View {campaigns.length - 3} more campaigns
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Hash className="w-8 h-8 text-gray-400" />
                  </div>
                      <p className="text-gray-500 dark:text-gray-400 mb-4">No campaigns linked to this release</p>
                      {release?.id ? (
                        <div className="space-y-2">
                          <button
                            onClick={handleCreateCampaign}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium w-full justify-center"
                          >
                            <Hash className="w-4 h-4" />
                            Create Campaign
                          </button>
                          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">or</div>
                          <button
                            onClick={handleLinkCampaign}
                            className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium w-full"
                          >
                            Link Existing Campaign
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                          Save this release first to create campaigns
                        </p>
                      )}
                  </div>
                )}
                </div>

                {/* Team Contacts Card */}
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Team Contacts
                      </h3>
                      {teamContacts.length > 0 && (
                        <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full text-xs font-medium">
                          {teamContacts.length}
                      </span>
                      )}
                    </div>
                    <button
                      onClick={handleOpenContactLinkModal}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    >
                      + Add Contact
                    </button>
                    </div>
                    
                  {teamContactsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : teamContacts.length > 0 ? (
                    <div className="space-y-3">
                      {teamContacts.map(contact => (
                        <div key={contact.linkId} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {contact.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white truncate">{contact.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {contact.clientName || 'Contact'}
                                </div>
                              </div>
                              <button
                                onClick={() => handleUnlinkContact(contact)}
                                className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Unlink
                              </button>
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {contact.role || (contact.isPrimary ? 'Primary Contact' : 'Team Member')}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {contact.email && (
                              <a
                                href={`mailto:${contact.email}`}
                                className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                title={`Email ${contact.name}`}
                              >
                                <Mail className="w-4 h-4" />
                              </a>
                            )}
                            {contact.phone && (
                              <a
                                href={`tel:${contact.phone}`}
                                className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                                title={`Call ${contact.name}`}
                              >
                                <Phone className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                        <User className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 mb-3">No team contacts found</p>
                      <button
                        onClick={handleOpenContactLinkModal}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                      <User className="w-4 h-4" />
                        Add Contact
                      </button>
                  </div>
                )}
              </div>
                        </div>
                      </div>
                    </div>

          {/* Action Toolbar */}
          {modalState !== 'view' && (
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
              {/* Edit/Create Mode - Save Actions */}
              <div className="p-6 flex items-center justify-between">
                <div className="flex gap-3">
                  {release && (
                    <>
                      <button
                        onClick={handleArchive}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors"
                      >
                        Archive
                      </button>
                      <button
                        onClick={handleDuplicate}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        Duplicate
                      </button>
                    </>
                )}
              </div>
                <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave}
                    className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {modalState === 'create' ? 'Create Release' : 'Save Changes'}
                  </>
                )}
              </button>
                </div>
            </div>
            </div>
          )}
        </div>
      </div>

      {/* New Artist Creation Dialog */}
      {showNewArtistDialog && newArtistData && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 transition-opacity" />
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Add New Artist
                  </h3>
                  <button
                    onClick={() => setShowNewArtistDialog(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Artist Name
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-lg">
                      {newArtistData.name}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Client (Record Label/Manager) *
                    </label>
                    <select
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a client</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {newArtistData.image_url && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Artist Image
                      </label>
                      <img
                        src={newArtistData.image_url}
                        alt={newArtistData.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-3 pt-4">
                    <button
                      onClick={() => setShowNewArtistDialog(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateArtist}
                      disabled={!selectedClientId || isCreatingArtist}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      {isCreatingArtist ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Create Artist
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Linking Modal */}
      {showCampaignLinkModal && (
        <div className="fixed inset-0 z-70 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 transition-opacity" />
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                    <Hash className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Link Campaigns
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Select existing campaigns to link to this release
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCampaignLinkModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Search */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search campaigns..."
                    value={campaignSearchQuery}
                    onChange={(e) => setCampaignSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              {/* Campaign List */}
              <div className="flex-1 overflow-y-auto p-6">
                {campaignLinkLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                    <span className="ml-2 text-gray-500 dark:text-gray-400">Loading campaigns...</span>
                  </div>
                ) : campaignLinkError ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                    <p className="text-red-600 dark:text-red-400 mb-4">{campaignLinkError}</p>
                    <button
                      onClick={loadAvailableCampaigns}
                      className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                    >
                      Try again
                    </button>
                  </div>
                ) : availableCampaigns.length === 0 ? (
                  <div className="text-center py-12">
                    <Hash className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {campaignSearchQuery ? 'No campaigns found matching your search' : 'No available campaigns to link'}
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      Create new campaigns from the main Campaigns page
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableCampaigns.map((campaign) => (
                      <div
                        key={campaign.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-all ${
                          selectedCampaignIds.includes(campaign.id)
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                        onClick={() => {
                          setSelectedCampaignIds(prev => 
                            prev.includes(campaign.id)
                              ? prev.filter(id => id !== campaign.id)
                              : [...prev, campaign.id]
                          );
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedCampaignIds.includes(campaign.id)}
                              onChange={() => {}} // Handled by parent div click
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {campaign.name}
                            </h4>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            campaign.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            campaign.status === 'completed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>
                            {campaign.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div>Budget: ${(campaign.budget || 0).toLocaleString()}</div>
                          <div>Spend: ${(campaign.spend || 0).toLocaleString()}</div>
                          <div>
                            {campaign.start_date && new Date(campaign.start_date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedCampaignIds.length > 0 && (
                    <span>{selectedCampaignIds.length} campaign{selectedCampaignIds.length > 1 ? 's' : ''} selected</span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCampaignLinkModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLinkSelectedCampaigns}
                    disabled={selectedCampaignIds.length === 0 || campaignLinkLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {campaignLinkLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Linking...
                      </>
                    ) : (
                      <>
                        <Hash className="w-4 h-4" />
                        Link {selectedCampaignIds.length > 0 ? selectedCampaignIds.length : ''} Campaign{selectedCampaignIds.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Linking Modal */}
      {showContactLinkModal && (
        <div className="fixed inset-0 z-70 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 transition-opacity" />
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Link Contacts
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Choose team members to associate with this release
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowContactLinkModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={contactSearchQuery}
                    onChange={(e) => setContactSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {contactLinkLoading && availableTeamContacts.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    <span className="ml-2 text-gray-500 dark:text-gray-400">Loading contacts...</span>
                  </div>
                ) : contactLinkError ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                    <p className="text-red-600 dark:text-red-400 mb-4">{contactLinkError}</p>
                    <button
                      onClick={loadAvailableTeamContacts}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      Try again
                    </button>
                  </div>
                ) : filteredAvailableContacts.length === 0 ? (
                  <div className="text-center py-12">
                    <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {contactSearchQuery ? 'No contacts match this search' : 'No additional contacts available to link'}
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      Add contacts to the company from the Contacts page.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredAvailableContacts.map((contact) => {
                      const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                      const displayName = fullName || contact.email || 'Unnamed Contact';
                      return (
                        <div
                          key={contact.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            selectedContactIds.includes(contact.id)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                          onClick={() => handleToggleContactSelection(contact.id)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={selectedContactIds.includes(contact.id)}
                                onChange={() => handleToggleContactSelection(contact.id)}
                                onClick={(event) => event.stopPropagation()}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white">{displayName}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {contact.title || contact.department || (contact.is_primary ? 'Primary Contact' : 'Team Member')}
                                </div>
                              </div>
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-right">
                              {contact.email || contact.phone || '—'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedContactIds.length > 0 && (
                    <span>{selectedContactIds.length} contact{selectedContactIds.length > 1 ? 's' : ''} selected</span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowContactLinkModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLinkSelectedContacts}
                    disabled={selectedContactIds.length === 0 || contactLinkLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {contactLinkLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Linking...
                      </>
                    ) : (
                      <>
                        <User className="w-4 h-4" />
                        Link {selectedContactIds.length > 0 ? selectedContactIds.length : ''} Contact{selectedContactIds.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Release Dialog */}
      {showDuplicateDialog && duplicateRelease && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 transition-opacity" />
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Duplicate Release Found
                    </h3>
                  </div>
                  <button
                    onClick={handleCancelDuplicate}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    A release with similar details already exists in your system:
                  </p>

                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      {duplicateRelease.artwork_url && (
                        <img
                          src={duplicateRelease.artwork_url}
                          alt="Existing release artwork"
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {duplicateRelease.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {duplicateRelease.artist?.name}
                        </p>
                        {duplicateRelease.release_date && (
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            Released: {new Date(duplicateRelease.release_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Do you really want to add this as a duplicate release?
                  </p>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleCancelDuplicate}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProceedWithDuplicate}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
                    >
                      Add Duplicate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedReleaseModal;
